import { debounce, DebouncedFunc } from 'lodash';
import { makeGraphqlRequestCloud } from '@/api/graphql/graphQLClient';
import type { RequestDocument } from 'graphql-request';
import type { Callback, Dict, StrictNullable } from '@/types/utility';

type CreatePollingSubscriptionOptions<T> = {
  intervalMs?: number;
  debounceMs?: number;
  initialPaused?: boolean;
  maxConsecutiveErrors?: number;
  shouldSkip?: Callback<[], boolean>;
  fetch: Callback<[], Promise<T>>;
  onData: Callback<[T]>;
  onError?: Callback<[unknown]>;
};

type PollingSubscriptionStatus = {
  isSubscribed: boolean;
  isPaused: boolean;
  consecutiveErrors: number;
  isRequestInProgress: boolean;
};

type PollingSubscription = {
  unsubscribe: Callback;
  isConnected: Promise<void>;
  refresh: Callback;
  getStatus: Callback<[], PollingSubscriptionStatus>;
  pause: Callback;
  resume: Callback;
};

export const createPollingSubscription = <T>({
  intervalMs = 1000,
  debounceMs = 100,
  initialPaused = false,
  maxConsecutiveErrors = 5,
  shouldSkip = () => false,
  fetch,
  onData,
  onError = (err: unknown) => console.error('Polling subscription error:', err),
}: CreatePollingSubscriptionOptions<T>): PollingSubscription => {
  const isPageVisible = () => !document.hidden;

  const {
    promise: isConnectedPromise,
    resolve: resolveIsConnected,
    reject: rejectIsConnected,
  } = Promise.withResolvers<void>();

  let intervalId: StrictNullable<number> = null;
  let isSubscribed = true;
  let isPaused = initialPaused;
  let consecutiveErrors = 0;
  let isRequestInProgress = false;

  const debouncedTick: DebouncedFunc<Callback<[], Promise<void>>> = debounce(async () => {
    if (!isSubscribed || isRequestInProgress || !isPageVisible() || isPaused) {
      return;
    }

    if (shouldSkip()) {
      return;
    }

    isRequestInProgress = true;
    try {
      const data = await fetch();

      onData(data);

      resolveIsConnected();

      consecutiveErrors = 0;
    } catch (err) {
      onError(err);
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        // Stop polling on too many errors
        isSubscribed = false;
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        rejectIsConnected(new Error('Polling subscription failed: max consecutive errors reached'));
        return;
      }
    } finally {
      isRequestInProgress = false;
    }
  }, debounceMs);

  // const handleVisibilityChange = () => {
  //   if (isPageVisible() && isSubscribed && !isPaused) {
  //     debouncedTick();
  //   }
  // };

  // Make request immediately
  debouncedTick();
  intervalId = setInterval(debouncedTick, intervalMs);
  // document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    unsubscribe() {
      isSubscribed = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      debouncedTick.cancel();
      // document.removeEventListener('visibilitychange', handleVisibilityChange);
    },
    isConnected: isConnectedPromise,
    refresh() {
      if (isSubscribed) {
        debouncedTick.flush();
      }
    },
    getStatus(): PollingSubscriptionStatus {
      return {
        isSubscribed,
        isPaused,
        consecutiveErrors,
        isRequestInProgress,
      };
    },
    pause() {
      isPaused = true;
    },
    resume() {
      isPaused = false;
      if (isSubscribed && isPageVisible()) {
        debouncedTick();
      }
    },
  };
};

type GraphqlFetcherArgs = {
  document: RequestDocument;
  getVariables: Callback<[], Dict<unknown>>;
  shareToken?: string;
  sharedObjectId?: number;
};

type CreateGraphqlPollingSubscriptionOptions<TResponse> = Omit<
  CreatePollingSubscriptionOptions<TResponse>,
  'fetch'
> & {
  fetcher: GraphqlFetcherArgs;
};

export const createGraphqlPollingSubscription = <TResponse>({
  fetcher: { document, getVariables, shareToken, sharedObjectId },
  ...configuration
}: CreateGraphqlPollingSubscriptionOptions<TResponse>): PollingSubscription => {
  return createPollingSubscription<TResponse>({
    ...configuration,
    async fetch() {
      const response = (await makeGraphqlRequestCloud(
        document,
        getVariables(),
        1,
        shareToken,
        sharedObjectId,
      )) as TResponse;
      return response;
    },
  });
};
